// src/integrations/formPortability/index.js

import {
  bitlyFormPortabilityPolicy,
} from '../providers/bitly/bitlyFormPortability';
import {
  bigQueryFormPortabilityPolicy,
} from '../providers/bigquery/bigQueryFormPortability';

export const FORM_DUPLICATE_INTEGRATION_POLICY = Object.freeze({
  STRIP: 'strip',
  COPY_WITH_VALIDATION: 'copy_with_validation',
  BLOCK: 'block',
});

const REGISTERED_FORM_PORTABILITY_POLICIES = Object.freeze([
  bitlyFormPortabilityPolicy,
  bigQueryFormPortabilityPolicy,
]);

function isPlainObject(value) {
  return Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value);
}

/**
 * Applies provider-owned duplicate policies to form submission settings.
 *
 * Enterprise contract:
 * - Duplicates copy form design and normal submission workflow.
 * - Duplicates do not inherit executable provider state.
 * - Provider-specific cleanup lives in provider modules, not duplicateForm.js.
 * - Future providers, such as HubSpot, add their own policy without changing
 *   the duplicate pipeline.
 */
export function sanitizeSubmissionSettingsForDuplicate(
  submissionSettings,
  context = {}
) {
  if (!isPlainObject(submissionSettings)) {
    return {
      submissionSettings,
      diagnostics: [],
    };
  }

  let nextSubmissionSettings = {
    ...submissionSettings,
  };

  const diagnostics = [];

  REGISTERED_FORM_PORTABILITY_POLICIES.forEach((policy) => {
    if (
      !policy ||
      typeof policy.sanitizeSubmissionSettingsForDuplicate !== 'function'
    ) {
      return;
    }

    const result = policy.sanitizeSubmissionSettingsForDuplicate(
      nextSubmissionSettings,
      context
    );

    if (!result || !isPlainObject(result)) {
      return;
    }

    nextSubmissionSettings =
      result.settings && isPlainObject(result.settings)
        ? result.settings
        : nextSubmissionSettings;

    diagnostics.push({
      providerId: policy.providerId || 'unknown',
      duplicatePolicy: policy.duplicatePolicy ||
        FORM_DUPLICATE_INTEGRATION_POLICY.STRIP,
      ...(result.diagnostics || {}),
    });
  });

  return {
    submissionSettings: nextSubmissionSettings,
    diagnostics,
  };
}

/**
 * Future-proof list for server/client duplication flows that may eventually
 * copy form subcollections.
 *
 * Current duplicateForm.js only seeds local builder storage from root form
 * blobs, but this exclusion list prevents accidental provider-runtime copying
 * if a future duplication implementation starts cloning subcollections.
 */
export function getIntegrationSubcollectionsExcludedFromDuplicate() {
  return new Set([
    'integrationConfigs',
    'integrationConfig',
    'integrationAuditLogs',
    'integrationJobs',
    'submissionShortlinkQueue',
  ]);
}

export function getRegisteredFormPortabilityPolicies() {
  return REGISTERED_FORM_PORTABILITY_POLICIES.map((policy) => ({
    providerId: policy.providerId || 'unknown',
    duplicatePolicy: policy.duplicatePolicy ||
      FORM_DUPLICATE_INTEGRATION_POLICY.STRIP,
  }));
}